import { size } from 'lodash';

import {
  getCookie,
  setCookie,
  SameSite,
  deleteCookie
} from 'lib/utils/cookieUtils';

import { ADS_PROPERTIES, ALL_ATTRIBUTION_PROPERTIES, CAMPAIGN_PROPERTIES, EXTERNAL_REFERRER, PAGE_PROPERTIES, PAGE_PROPERTIES_MAP } from './attributionProperties';

const URL_ATTRIBUTION_PROPERTIES = [...CAMPAIGN_PROPERTIES, ...ADS_PROPERTIES];

interface AttributionValues {
  [key: string]: string;
}

const attachClientAttributionData = () => {
  const urlParams = new URLSearchParams(window.location.search);

  // exits early if no attribution properties are present on the URL or is not an external referrer
  if (!hasUrlAttributionPropertiesPresent(urlParams)) {
    return;
  }

  // if any of the previously captured values are different, we remove all stored properties and rewrite them based on the new url
  if (haveValuesChanged(urlParams)) {
    clearAttributionSessionStorage();
  }

  // sets campaign properties
  setSessionStorageForProperties(urlParams, CAMPAIGN_PROPERTIES);

  // sets ads properties
  setSessionStorageForProperties(urlParams, ADS_PROPERTIES);

  // Sets page properties on session
  setSessionStorageForPageProperties();
};

const clearAttributionSessionStorage = () => {
  ALL_ATTRIBUTION_PROPERTIES.forEach((name) => {
    deleteCookie(name);
  });
};

const setSessionStorageForProperties = (
  urlParams: URLSearchParams,
  properties: Array<string>
): void => {
  for (const property of properties) {
    const value = urlParams.get(property);

    if (value) {
      setAttributionSessionStorage(property, value);
    }
  }
};

const setAttributionSessionStorage = (name: string, value: string): void => {
  setCookie(name, value, undefined, undefined, SameSite.STRICT);
};

const hasUrlAttributionPropertiesPresent = (
  urlParams: URLSearchParams
): boolean => {
  if (hasExternalReferrer()) {
    return true;
  }

  const urlParamsArray = Array.from(urlParams.keys());
  const hasAttributes = urlParamsArray.some((param) =>
    URL_ATTRIBUTION_PROPERTIES.includes(param)
  );

  // Check for existing values that might need cleanup
  const hasExistingValues = hasValuesPresent();
  const storedReferrer = getCookie(EXTERNAL_REFERRER);

  return hasAttributes || hasExistingValues || storedReferrer !== null;
};

const hasExternalReferrer = (): boolean => {
  const { referrer } = document;

  // This avoids a bug where we attach the Trolley or Persona URL leading to a large cookie size
  // This check must occur first
  if (isThirdPartyUserVerificationReferrer(referrer)) {
    return false;
  }

  // only sets page properties when referrer is external
  if (referrer && !referrer.includes('oneflare.com.au')) {
    return true;
  }

  return false;
};

const haveValuesChanged = (urlParams: URLSearchParams): boolean => {
  const { referrer } = document;
  const storedReferrer = getCookie(EXTERNAL_REFERRER);

  // Check if referrer has changed
  if (storedReferrer && storedReferrer !== referrer) {
    return true;
  }

  const valuesChanged = URL_ATTRIBUTION_PROPERTIES.some((property) => {
    const currentValue = getAttributionValue(property);
    const incomingValue = urlParams.get(property);

    return currentValue !== null && currentValue !== incomingValue;
  });

  return valuesChanged;
};

const hasValuesPresent = (): boolean => {
  if (size(getAllAdsValues()) > 0) {
    return true;
  }
  if (size(getAllCampaignValues()) > 0) {
    return true;
  }

  return false;
};

const isThirdPartyUserVerificationReferrer = (
  referrer: string | string[]
): boolean => {
  const thirdPartyDomains: string[] = [
    'account/verify-account',
    'oneflare.withpersona.com',
    'widget.trolley.com'
  ];

  return thirdPartyDomains.some((domain) => referrer.includes(domain));
};

const getAttributionValue = (name: string): string | null => getCookie(name);

const getAllCampaignValues = (): AttributionValues =>
  CAMPAIGN_PROPERTIES.reduce<AttributionValues>((values, prop) => {
    const value = getAttributionValue(prop);

    return value !== null ? { ...values, [prop]: value } : values;
  }, {});

const getAllAdsValues = (): AttributionValues =>
  ADS_PROPERTIES.reduce<AttributionValues>((values, prop) => {
    const value = getAttributionValue(prop);

    return value !== null ? { ...values, [prop]: value } : values;
  }, {});

const getAllPageValues = (): AttributionValues =>
  PAGE_PROPERTIES.reduce<AttributionValues>((values, prop) => {
    const value = getAttributionValue(prop);

    return value !== null ? { ...values, [prop]: value } : values;
  }, {});

const setSessionStorageForPageProperties = (): void => {
  // only sets page properties when referrer is external
  if (hasExternalReferrer()) {
    setAttributionSessionStorage(EXTERNAL_REFERRER, document.referrer);

    for (const prop of PAGE_PROPERTIES_MAP.keys()) {
      const retrieverFunction = PAGE_PROPERTIES_MAP.get(prop);
      if (retrieverFunction) {
        setAttributionSessionStorage(prop, retrieverFunction());
      }
    }
  }
};

export {
  attachClientAttributionData,
  getAllPageValues,
  getAllAdsValues,
  getAllCampaignValues
};

export type { AttributionValues };
